import { reactive } from 'vue'
import Page from '@/components/page'

import pubsub from '@/lib/pubsub'
import { checkStatus, parseJson, getHeaders } from '@/lib/net'
import urls, { STANDARD_TRIAL_PARAM, HUNT_ELITE_TRIAL_PARAM } from '@/lib/urls'
import { goToPurchase } from '@/lib/purchase-utils'

import { saveItem } from '@/plugins/storage'

import subscriptionService from '@/services/subscription-service'

import { htmlToElement } from '@/lib/dom'

import bigLogoImage from '@/assets/images/big-logo.png'
import { PURCHASE_CTA_ORIGINS } from '@/lib/enums-marketing'

export const storage = reactive({
  trialRenewed: false,
})

/** Class responsible for dealing with an account */
export default class Account {
  /**
   * @constructor
   */
  constructor() {
    this.environment = window.environment

    const loginPage = window.location.href.includes('login')
    const createAccountPage = window.location.href.includes('account')

    if (!loginPage && !createAccountPage) {
      if (urls.getTrialParam()) {
        this.requestTrial(null, STANDARD_TRIAL_PARAM)
      } else if (urls.getEliteTrialParam()) {
        this.requestTrial(null, HUNT_ELITE_TRIAL_PARAM)
      }
    }

    this.page = new Page()
    this.page.check()
  }

  /**
   * Request the number of trials the user has currently used from the server
   */
  async getTrialStatus() {
    const options = {
      method: 'GET',
      headers: await getHeaders(),
    }

    fetch(this.environment.apiUrl + '/v1/trials', options)
      .then(checkStatus)
      .then(parseJson)
      .then((response) => {
        pubsub.publish('accountTrials', {
          used: response.used,
          remaining: response.remaining,
        })
      })
  }

  /**
   * Request a new trial for the user from the server. This may or may not
   * be denied depending on the number of trials they've currently used (3 max).
   *
   * @param {string} state - state param to allow the user to change
   * states for the their next trial; otherwise it sends their current state
   * @param {string} trialParam - trial query param that specifies type of trial
   */
  async requestTrial(state = null, trialParam) {
    const options = {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify({
        ...state,
        start_elite_trial: trialParam === HUNT_ELITE_TRIAL_PARAM,
      }),
    }

    fetch(this.environment.apiUrl + '/v1/trials', options)
      .then(checkStatus)
      .then(parseJson)
      .then(() => {
        storage.trialRenewed = true
        const app = 'fish'
        // don't show autosub modal if user is requesting a trial
        saveItem(`${app}SeenAutosubscribe`, true)
        if (trialParam === STANDARD_TRIAL_PARAM) {
          saveItem(`${app}SubmittedOnboardingSteps`, [])
        }
        urls.removeParam(trialParam)
        subscriptionService.syncSubscriptions()
      })
      .catch((error) => {
        urls.removeParam(trialParam)
        this.extraTrialDenied(error)
      })
  }

  /**
   * Handle when an extra trial is denied
   *
   * @param {object} error - error returned from the server
   */
  extraTrialDenied(error) {
    const errorMessage = error.errors.reason || error.errors.state

    if (document.body.contains(this.selectorDialog)) {
      document.body.removeChild(this.selectorDialog)
    }

    const trialDeniedDialog = htmlToElement(
      `<div class="dialog-overlay big">
        <div id="trial-denied-dialog" class="dialog big-dialog" role="dialog" tabindex="0">
          <div class="dialog-header">
            <div class="logo"><img src="${bigLogoImage}"></div>
            <div class="title"><h2>Welcome Back to onX</h2></div>
            <div class="dialog-close" title="Close Dialog">
              <div class="close">
                <div class="cross-one"></div>
                <div class="cross-two"></div>
              </div>
            </div>
          </div>

          <div class="big-dialog-content"></div>
        </div>
      </div>`
    )

    let message = ''
    let buttonText = ''

    if (errorMessage && errorMessage === 'too many trials') {
      message =
        'It looks like you have used all available trials. Get your membership today.'
      buttonText = 'Join now'
    } else if (errorMessage && errorMessage === 'active membership') {
      if (subscriptionService.isTrial()) {
        message = 'You are already in a trial.'
        buttonText = 'Take me to the app'
      } else if (
        subscriptionService.isPremium() ||
        subscriptionService.isPremiumMultiState()
      ) {
        message =
          'You are already a Premium Hunt Member, would you like to upgrade to Elite?'
        buttonText = 'Go Elite'
      } else if (
        subscriptionService.isElite() ||
        subscriptionService.isEmployee()
      ) {
        message = 'You are already an Elite Member.'
        buttonText = 'Take me to the app'
      }
    }
    // This case also includes the error message "state is invalid", but this
    // indicates an error on the client or API so they should contact CS. Otherwise,
    // this case is primarily if the server goes down or something similar.
    else {
      message =
        'Something went wrong activating your trial. Please contact customer service.'
      buttonText = 'Ok'
    }

    const content = htmlToElement(
      `<div class="additional-trial-content">
        <h2>${message}</h2>
        <button class="additional-trial-button">${buttonText}</button>
      </div>`
    )

    trialDeniedDialog.querySelector('.big-dialog-content').appendChild(content)

    trialDeniedDialog.querySelector('.close').addEventListener('click', () => {
      document.body.removeChild(trialDeniedDialog)
    })

    trialDeniedDialog
      .querySelector('.additional-trial-button')
      .addEventListener('click', () => {
        if (buttonText === 'Take me to the app') {
          document.body.removeChild(trialDeniedDialog)
        } else {
          goToPurchase({
            name: PURCHASE_CTA_ORIGINS.TRIAL_DENIED,
            detail: 'Join now',
          })
        }
      })

    urls.removeParam('request_trial')

    document.body.appendChild(trialDeniedDialog)
  }
}
